<template>
  <div class="mt-2">
    <v-card v-if="!pdfExportMode" outlined class="pa-3 mb-3 filter-card">
      <v-row dense align="center">
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="filterSearch"
            dense
            clearable
            hide-details
            prepend-inner-icon="mdi-magnify"
            :label="$t('diagSystemAdoption.groupFilters.search', { field: groupHeader })"
          />
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-text-field
            v-model.number="filterMinAdherence"
            dense
            type="number"
            min="0"
            max="100"
            hide-details
            :label="$t('diagSystemAdoption.groupFilters.minAdherence')"
            suffix="%"
          />
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-text-field
            v-model.number="filterMaxAdherence"
            dense
            type="number"
            min="0"
            max="100"
            hide-details
            :label="$t('diagSystemAdoption.groupFilters.maxAdherence')"
            suffix="%"
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="filterSort"
            dense
            hide-details
            :items="sortOptions"
            item-text="text"
            item-value="value"
            :label="$t('diagSystemAdoption.groupFilters.sortBy')"
          />
        </v-col>
        <v-col cols="12" sm="6" md="2" class="d-flex align-center">
          <v-btn text small color="primary" @click="clearFilters">
            {{ $t('diagSystemAdoption.groupFilters.clear') }}
          </v-btn>
        </v-col>
      </v-row>
      <div class="caption grey--text mt-2">
        {{ $t('diagSystemAdoption.groupFilters.showingCount', { shown: filteredRows.length, total: (rows || []).length }) }}
      </div>
    </v-card>

    <v-card ref="chartCard" class="pa-4 mb-4 chart-card">
      <div class="d-flex align-center justify-space-between flex-wrap mb-2">
        <div class="text-subtitle-1 font-weight-bold">
          {{ title }} — {{ $t('diagSystemAdoption.charts.adherenceByGroup') }}
        </div>
        <div v-if="!pdfExportMode" class="d-flex align-center flex-wrap">
          <v-select
            v-model="chartPageSize"
            dense
            hide-details
            class="chart-page-size mr-3"
            :items="pageSizeOptions"
            :label="$t('diagSystemAdoption.groupFilters.perPage')"
            style="max-width: 110px"
          />
          <v-btn icon small :disabled="chartPage <= 1" @click="chartPage -= 1">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <span class="caption mx-2">
            {{ $t('diagSystemAdoption.groupFilters.pageOf', { page: chartPage, pages: chartTotalPages }) }}
          </span>
          <v-btn icon small :disabled="chartPage >= chartTotalPages" @click="chartPage += 1">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
        <div v-else class="caption grey--text">
          {{ $t('diagSystemAdoption.groupFilters.pageOf', { page: chartPage, pages: chartTotalPages }) }}
        </div>
      </div>
      <apexchart
        v-if="pagedChartRows.length"
        :key="'chart-' + chartPage + '-' + chartPageSize + '-' + filterKey + '-' + (pdfExportMode ? 'pdf' : 'ui')"
        type="bar"
        :height="chartHeight"
        :options="chartOptions"
        :series="chartSeries"
      />
      <div v-else class="caption grey--text py-8 text-center">
        {{ $t('diagSystemAdoption.charts.noData') }}
      </div>
    </v-card>

    <v-card ref="tableCard" class="pa-2 chart-card">
      <v-data-table
        dense
        :headers="tableHeaders"
        :items="tableItems"
        :items-per-page="tablePageSize"
        :hide-default-footer="pdfExportMode"
        :footer-props="tableFooterProps"
        class="elevation-0"
      >
        <template v-slot:item.aderencia_pct="{ item }">
          <strong>{{ formatPct(item.aderencia_pct) }}</strong>
        </template>
        <template v-slot:item.passageiros="{ item }">
          {{ formatNumber(item.passageiros) }}
        </template>
        <template v-slot:item.aderiram_periodo="{ item }">
          {{ formatNumber(item.aderiram_periodo) }}
        </template>
        <template v-slot:item.nunca_aderiram="{ item }">
          {{ item.nunca_aderiram != null ? formatNumber(item.nunca_aderiram) : '—' }}
        </template>
        <template v-slot:item.inativos="{ item }">
          {{ item.inativos != null ? formatNumber(item.inativos) : '—' }}
        </template>
        <template v-slot:item.unico_checkin="{ item }">
          {{ item.unico_checkin != null ? formatNumber(item.unico_checkin) : '—' }}
        </template>
        <template v-slot:item.embarques_periodo="{ item }">
          {{ formatNumber(item.embarques_periodo) }}
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
function formatNumber(n) {
  const v = Number(n) || 0;
  return v.toLocaleString('pt-BR');
}

function formatPct(n) {
  if (n == null || n === '') return '—';
  const v = Number(n) || 0;
  return `${v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

export default {
  name: 'DIAGAdocaoSistemaGrupo',
  props: {
    title: { type: String, default: '' },
    rows: { type: Array, default: () => [] },
    groupHeader: { type: String, default: '' },
    compact: { type: Boolean, default: false },
  },
  data: () => ({
    filterSearch: '',
    filterMinAdherence: null,
    filterMaxAdherence: null,
    filterSort: 'aderencia_desc',
    chartPage: 1,
    chartPageSize: 10,
    tablePageSize: 10,
    pageSizeOptions: [5, 10, 15, 20, 30],
    pdfExportMode: false,
    _pdfSavedChartPage: null,
    _pdfSavedTablePageSize: null,
  }),
  methods: {
    formatNumber,
    formatPct,
    clearFilters() {
      this.filterSearch = '';
      this.filterMinAdherence = null;
      this.filterMaxAdherence = null;
      this.filterSort = 'aderencia_desc';
      this.chartPage = 1;
    },
    /**
     * Prepara o componente para PDF: esconde filtros/controles e expande a tabela.
     * Mantém a paginação do gráfico (cada página será capturada separadamente).
     */
    async prepareForPdfExport() {
      if (this.pdfExportMode) return;
      this._pdfSavedChartPage = this.chartPage;
      this._pdfSavedTablePageSize = this.tablePageSize;
      this.pdfExportMode = true;
      this.tablePageSize = Math.max((this.filteredRows || []).length, 1);
      await this.$nextTick();
    },
    getChartTotalPages() {
      return this.chartTotalPages;
    },
    async goToChartPage(page) {
      const total = this.chartTotalPages;
      this.chartPage = Math.min(Math.max(1, page), total);
      await this.$nextTick();
    },
    getChartEl() {
      const ref = this.$refs.chartCard;
      return ref && ref.$el ? ref.$el : ref || null;
    },
    getTableEl() {
      const ref = this.$refs.tableCard;
      return ref && ref.$el ? ref.$el : ref || null;
    },
    restoreAfterPdfExport() {
      if (!this.pdfExportMode) return;
      this.tablePageSize = this._pdfSavedTablePageSize != null ? this._pdfSavedTablePageSize : 10;
      this.chartPage = this._pdfSavedChartPage != null ? this._pdfSavedChartPage : 1;
      this.pdfExportMode = false;
      this._pdfSavedChartPage = null;
      this._pdfSavedTablePageSize = null;
    },
  },
  computed: {
    sortOptions() {
      return [
        { value: 'aderencia_desc', text: this.$t('diagSystemAdoption.groupFilters.sortAdherenceDesc') },
        { value: 'aderencia_asc', text: this.$t('diagSystemAdoption.groupFilters.sortAdherenceAsc') },
        { value: 'passageiros_desc', text: this.$t('diagSystemAdoption.groupFilters.sortPassengersDesc') },
        { value: 'passageiros_asc', text: this.$t('diagSystemAdoption.groupFilters.sortPassengersAsc') },
        { value: 'label_asc', text: this.$t('diagSystemAdoption.groupFilters.sortLabelAsc') },
        { value: 'label_desc', text: this.$t('diagSystemAdoption.groupFilters.sortLabelDesc') },
      ];
    },
    filterKey() {
      return [
        this.filterSearch,
        this.filterMinAdherence,
        this.filterMaxAdherence,
        this.filterSort,
      ].join('|');
    },
    filteredRows() {
      let list = [...(this.rows || [])];
      const q = (this.filterSearch || '').trim().toLowerCase();
      if (q) {
        list = list.filter((r) => String(r.grupo_label || '').toLowerCase().includes(q));
      }
      const minA = this.filterMinAdherence;
      if (minA != null && minA !== '' && !Number.isNaN(Number(minA))) {
        list = list.filter((r) => Number(r.aderencia_pct) >= Number(minA));
      }
      const maxA = this.filterMaxAdherence;
      if (maxA != null && maxA !== '' && !Number.isNaN(Number(maxA))) {
        list = list.filter((r) => Number(r.aderencia_pct) <= Number(maxA));
      }

      const sort = this.filterSort || 'aderencia_desc';
      list.sort((a, b) => {
        if (sort === 'aderencia_desc') return Number(b.aderencia_pct) - Number(a.aderencia_pct);
        if (sort === 'aderencia_asc') return Number(a.aderencia_pct) - Number(b.aderencia_pct);
        if (sort === 'passageiros_desc') return Number(b.passageiros) - Number(a.passageiros);
        if (sort === 'passageiros_asc') return Number(a.passageiros) - Number(b.passageiros);
        const la = String(a.grupo_label || '').localeCompare(String(b.grupo_label || ''), 'pt', { sensitivity: 'base' });
        if (sort === 'label_asc') return la;
        if (sort === 'label_desc') return -la;
        return 0;
      });
      return list;
    },
    chartTotalPages() {
      const size = Number(this.chartPageSize) || 10;
      return Math.max(1, Math.ceil(this.filteredRows.length / size));
    },
    pagedChartRows() {
      const size = Number(this.chartPageSize) || 10;
      const page = Math.min(Math.max(1, this.chartPage), this.chartTotalPages);
      const start = (page - 1) * size;
      return this.filteredRows.slice(start, start + size);
    },
    chartHeight() {
      const h = Math.max(220, this.pagedChartRows.length * 36 + 80);
      return Math.min(520, h);
    },
    chartSeries() {
      return [
        {
          name: this.$t('diagSystemAdoption.group.adherence'),
          data: this.pagedChartRows.map((r) => Number(r.aderencia_pct) || 0),
        },
      ];
    },
    chartOptions() {
      return {
        chart: { type: 'bar', toolbar: { show: false }, animations: { enabled: false } },
        plotOptions: {
          bar: { horizontal: true, borderRadius: 3, barHeight: '70%' },
        },
        colors: ['#1565c0'],
        dataLabels: {
          enabled: true,
          formatter: (v) => formatPct(v),
        },
        xaxis: {
          categories: this.pagedChartRows.map((r) => r.grupo_label),
          max: 100,
          labels: { formatter: (v) => `${Number(v).toFixed(0)}%` },
        },
        yaxis: {
          labels: { maxWidth: 180 },
        },
        tooltip: {
          y: { formatter: (v) => formatPct(v) },
        },
      };
    },
    tableHeaders() {
      const headers = [
        { text: this.groupHeader, value: 'grupo_label', sortable: true },
        { text: this.$t('diagSystemAdoption.group.passengers'), value: 'passageiros', sortable: true },
        { text: this.$t('diagSystemAdoption.group.adhered'), value: 'aderiram_periodo', sortable: true },
        { text: this.$t('diagSystemAdoption.group.adherence'), value: 'aderencia_pct', sortable: true },
      ];
      if (!this.compact) {
        headers.push(
          { text: this.$t('diagSystemAdoption.group.never'), value: 'nunca_aderiram', sortable: true },
          { text: this.$t('diagSystemAdoption.group.inactive'), value: 'inativos', sortable: true },
          { text: this.$t('diagSystemAdoption.group.single'), value: 'unico_checkin', sortable: true }
        );
      }
      headers.push({
        text: this.$t('diagSystemAdoption.group.boardings'),
        value: 'embarques_periodo',
        sortable: true,
      });
      return headers;
    },
    tableItems() {
      return this.filteredRows;
    },
    tableFooterProps() {
      return {
        'items-per-page-options': this.pageSizeOptions,
        'show-current-page': true,
        'show-first-last-page': true,
      };
    },
  },
  watch: {
    filterKey() {
      this.chartPage = 1;
    },
    chartPageSize() {
      this.chartPage = 1;
    },
    rows() {
      this.chartPage = 1;
    },
    chartTotalPages(pages) {
      if (this.chartPage > pages) this.chartPage = pages;
    },
  },
};
</script>

<style scoped>
.filter-card,
.chart-card {
  border-radius: 10px;
}
.chart-page-size {
  max-width: 110px;
}
</style>
